import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { IReplaceCardContract } from '@app/shared/models/banking.model';
import { ICard, ICardReasonsParams, IContract, IContractCancelReason } from '@app/shared/models/contract.model';
import { IDynamicDialogConfig } from '@app/shared/modules/dynamic-dialog/model';
import { SubscriberComponent } from '@app/shared/modules/subscriber/base/subscriber.component.base';
import { AmplitudeService } from '@app/shared/services/amplitude.service';
import { ContractContext } from '@app/shared/services/context/contract.context.service';
import { CustomerContext } from '@app/shared/services/context/customer.context.service';
import { ModalService } from '@app/shared/services/modal.service';
import { NotificationService } from '@app/shared/services/notification.service';
import { CardBasicService } from '@app/shared/services/wallet/card-basic.service';
import { CARD_TYPE, CONTRACT_TYPE } from '@app/shared/types/enums';
import { ACTION_EVENT_PROPERTY, AMPLITUDE_EVENT, ACTION_NAME } from '@app/shared/types/enums/amplitude';
import { AuthService } from '@auth0/auth0-angular';
import { TranslateService } from '@ngx-translate/core';
import { catchError, EMPTY, filter, Observable, switchMap, takeUntil, tap, throwError } from 'rxjs';

@Component({
  selector: 'app-card-replacement',
  templateUrl: './card-replacement.component.html',
  styleUrls: ['./card-replacement.component.sass'],
})
export class CardReplacementComponent extends SubscriberComponent implements OnInit, OnDestroy {
  public replacementForm!: FormGroup;
  public reasons$: Observable<IContractCancelReason[] | undefined> = EMPTY;
  initialState!: IDynamicDialogConfig;
  currentCard!: ICard | undefined;
  contract!: IContract | null;
  contractType!: CONTRACT_TYPE;
  cardType!: CARD_TYPE;
  private readonly productType = 'CARD';
  CARD_TYPE = CARD_TYPE;

  constructor(
    private readonly _amplitudeSvc: AmplitudeService,
    private readonly _modalSvc: ModalService,
    public readonly _contractContextService: ContractContext,
    private readonly _authSvc: AuthService,
    private readonly _cardBasicSvc: CardBasicService,
    private readonly _notificationSvc: NotificationService,
    private readonly _translateSvc: TranslateService,
    private readonly _customerCtxSvc: CustomerContext
  ) {
    super();
  }

  ngOnInit() {
    this.logInitEvent();
    this.getModalData();
    this.createForm();
    this.getContractAndCard();
    this.getBlockUnblockReasons();
    this.replaceCardSubscription();
  }

  private logInitEvent(): void {
    this._amplitudeSvc.logEvent<ACTION_EVENT_PROPERTY>(AMPLITUDE_EVENT.ACTION_INIT, {
      actionName: ACTION_NAME.REPLACE_CARD,
    });
  }

  getContractAndCard() {
    this.contract = this._contractContextService.getCurrentContract();
    this.contractType = this.contract?.type === CONTRACT_TYPE.CREDIT ? CONTRACT_TYPE.CREDIT : CONTRACT_TYPE.DEBIT;

    let {
      modalComponentData: { cardCode },
    } = this.initialState;

    this.currentCard = this.contract?.cards.find(card => card.code === cardCode);
    this.cardType = this.currentCard?.type === CARD_TYPE.VIRTUAL ? CARD_TYPE.VIRTUAL : CARD_TYPE.PHYSICAL;
  }

  private replaceCardSubscription(): void {
    this._modalSvc.isPrimaryActionClicked$
      .pipe(
        takeUntil(this.destroy$),
        filter(value => value),
        filter(() => this.replacementForm.valid),
        switchMap(() => this._authSvc.user$),
        switchMap(agentData => this.replaceCardRequest(agentData)),
        tap(() => this.handleReplaceCardSuccess()),
        catchError(() => this.handleReplaceCardError())
      )
      .subscribe();
  }

  getBlockUnblockReasons() {
    const params: ICardReasonsParams = {
      contract_type: this.contractType,
      product_type: this.productType,
      card_type: this.cardType,
    };
    this.reasons$ = this._cardBasicSvc.getBlockUnblockReasons(params);
  }

  createForm() {
    this.replacementForm = new FormGroup({
      reason: new FormControl('', Validators.required),
    });
    this.onChangesForm();
  }

  getModalData() {
    this._subscriptions.sink = this._modalSvc.getModalData().subscribe((data: IDynamicDialogConfig) => (this.initialState = data));
  }

  replaceCardRequest(agentData: any) {
    const agent_email = agentData?.email || '';
    const rppUserId = this._customerCtxSvc.getRppUserId();

    const params: IReplaceCardContract = {
      card_code: this.currentCard?.code || '',
      reason: this.replacementForm.get('reason')?.value || '',
      rpp_user_id: rppUserId || '',
      agent_email,
    };

    if (this.contractType === CONTRACT_TYPE.DEBIT) {
      return this._cardBasicSvc.replaceDebitCard(params);
    }

    return this._cardBasicSvc.replaceCard(params);
  }

  handleReplaceCardError() {
    this._modalSvc.closeWithErrorMsg();
    this.notification(this._translateSvc.instant('MODAL_CARDS_REPLACEMENT.SNACK_BAR.ERROR'), 3000, 'error', ['snackbar-failure']);

    return throwError(() => new Error('Replace card failed'));
  }

  handleReplaceCardSuccess() {
    this._modalSvc.closeWithSuccessMsg();
    this.notification(
      this._translateSvc.instant('MODAL_CARDS_RESTART_RESEND.SNACK_BAR.SUCCESS'),
      3000,
      'green_check',
      ['snackbar-success'],
      true
    );
  }

  notification(message: string, time: number, icon: string, theme: string[], isSVG = false) {
    this._notificationSvc.openSnackBar(message, time, undefined, undefined, icon, theme, isSVG);
  }

  onChangesForm() {
    this._subscriptions.sink = this.replacementForm.valueChanges.subscribe(() => {
      const data = {
        ...this.initialState,
        modalComponentData: {
          ...this.initialState?.modalComponentData,
          acceptButtonTitleDisabled: this.replacementForm.invalid,
        },
      };

      this._modalSvc.setModalData(data);
    });
  }

  ngOnDestroy() {
    this.unsubscribe();
  }
}
